import { ROUND } from "./financeUtils.mjs"
import { replaceASCII } from "./stringUtils.mjs"
import writtenNumber from "./written-number/index.mjs"

/**
 * Extracts the first number found in a string as a number type.
 *
 * If the string starts with a "-", the extracted number is negated.
 * Returns `undefined` if no number is found or if input is falsy.
 *
 * @param {string} str - The string to extract the number from.
 * @returns {number|undefined} The extracted number or undefined if none found.
 *
 * @example
 * extractNumberFromString("abc123def") // 123
 * extractNumberFromString("-abc123def") // -123
 * extractNumberFromString("no numbers") // undefined
 */
function extractNumberFromString(str) {
  if (!str) return undefined
  let [num] = (str.match(/\d+/g) || []).map(Number)
  if (str.startsWith("-")) num = -num
  return num
}

/**
 * Pads a number with leading zeros to ensure it has at least a specified length.
 *
 * If the number's length is already equal to or greater than the desired size, it is returned as is.
 *
 * @param {number} number - The number to pad.
 * @param {number} [size=2] - The minimum desired length of the resulting string.
 * @returns {string|number} The padded number as a string, or the original number if padding is not needed.
 *
 * @example
 * padNumber(5) // "05"
 * padNumber(123, 5) // "00123"
 * padNumber(1234, 3) // 1234
 */
function padNumber(number, size = 2) {
  return number < Math.pow(10, size - 1) ? "0".repeat(size - number.toString().length) + number : number
}

/**
 * Formats a number as a percentage string according to a given locale and options.
 *
 * - Returns the input as is if it is `null` or `undefined` (except zero).
 * - Avoids displaying `-0.00%` by normalizing very small negative numbers to zero.
 * - If `options` is `0`, formats percentage with no decimal digits.
 * - Defaults to at least 2 fraction digits if none specified.
 * - Special-cases the locale `"en-LT"` to `"lt-LT"`.
 *
 * @param {number} number - The number to format as a percentage (e.g., 0.25 for 25%).
 * @param {string} [locale] - The locale string for formatting (e.g., `"en-US"`).
 * @param {object|number} [options] - Intl.NumberFormat options or `0` to force no decimal digits.
 * @returns {string|number} The formatted percentage string, or the original input if invalid.
 *
 * @example
 * // Basic usage:
 * formatPercentage(0.1234, "en-US") // "12.34%"
 * formatPercentage(0.1234, "fr-FR", { minimumFractionDigits: 1 }) // "12,3 %"
 *
 * @example
 * // No decimals with options = 0:
 * formatPercentage(0.1234, "en-US", 0) // "12%"
 *
 * @example
 * // Handles special locale fix:
 * formatPercentage(0.5, "en-LT") // "50%"
 */
function formatPercentage(number, locale, options) {
  if (!number && number !== 0) return number

  // avoid -0.00
  if (Object.is(ROUND(number, 8), -0)) number = 0

  // this is a hack to make it simpler when we want no digits
  if (options === 0) return (100 * number).toFixed(0) + "%"
  else if (!options) options = {}

  if (!options.minimumFractionDigits) options.minimumFractionDigits = 2
  options.style = "percent"
  if (locale === "en-LT") locale = "lt-LT"
  return new Intl.NumberFormat(locale, options).format(number)
}

/**
 * Formats a number as a localized decimal string according to the given locale and options.
 *
 * - Returns the input unchanged if it is not a finite number (`isNumber` is false).
 * - Avoids displaying `-0.00` by normalizing very small negative numbers to zero.
 * - Defaults to a maximum of 4 fraction digits if not specified.
 * - Applies special character replacements for locales `"fr-GN"` and `"en-LT"`.
 *
 * @param {number} number - The number to format.
 * @param {string} [locale] - The locale string for formatting (e.g., `"en-US"`, `"fr-FR"`).
 * @param {object} [options] - Intl.NumberFormat options.
 * @returns {string|number} The formatted decimal string or the original input if falsy.
 *
 * @example
 * // Basic usage:
 * formatDecimal(1234.56789, "en-US") // "1,234.5679"
 * formatDecimal(1234.5, "fr-FR") // "1 234,5"
 *
 * @example
 * // Locale-specific replacements:
 * formatDecimal(1234.5, "fr-GN") // e.g. "1.234,5" (with replaced ASCII characters and dots)
 * formatDecimal(1234.5, "en-LT") // e.g. "1,234.5" with commas and dots swapped
 */
function formatDecimal(number, locale, options) {
  if (!isNumber(number)) return number

  /// avoid -0.00
  if (Object.is(ROUND(number, 8), -0)) number = 0

  if (!options) options = {}
  options.maximumFractionDigits = options.maximumFractionDigits ?? 4

  let formattedDecimal = new Intl.NumberFormat(locale, options).format(number)
  if (locale === "fr-GN") {
    formattedDecimal = replaceASCII(formattedDecimal)
    formattedDecimal = formattedDecimal.replace(/ /g, ".")
  }
  if (locale === "en-LT") {
    formattedDecimal = formattedDecimal.replace(new RegExp(/,/g), "#").replace(new RegExp(/\./), ",").replace(new RegExp(/#/g), ".")
  }

  return formattedDecimal
}

/**
 * Rounds a number to a specified number of decimal places accurately.
 * JavaScript floating-point arithmetic can produce rounding errors; this function mitigates them.
 *
 * Uses exponential notation to avoid floating-point precision issues.
 *
 * @param {number} number - The number to round.
 * @param {number} [decimalPlaces=2] - The number of decimal places to round to.
 * @returns {number} The rounded number.
 *
 * @example
 * roundAccurately(1.005) // 1.01
 * roundAccurately(1.23456, 3) // 1.235
 */
function roundAccurately(number, decimalPlaces = 2) {
  return Number(Math.round(number + "e" + decimalPlaces) + "e-" + decimalPlaces)
}

/**
 * Converts a number to its written (word) representation in a specified locale.
 *
 * Returns the input as is if it is `null` or `undefined` (except zero).
 *
 * @param {number} number - The number to convert to words.
 * @param {string} [locale] - The locale code (e.g., `"en-US"`, `"fr-FR"`). Only the language part before "-" is used.
 * @param {object} [options={}] - Additional options passed to the underlying number-to-words library.
 * @returns {string|number} The number spelled out in words, or the original input if invalid.
 *
 * @example
 * formatDecimalToWords(123, "en-US") // "one hundred twenty-three"
 * formatDecimalToWords(45, "fr-FR") // "quarante-cinq"
 * formatDecimalToWords(0) // "zero"
 */
function formatDecimalToWords(number, locale, options = {}) {
  if (!number && number !== 0) return number
  return writtenNumber(number, locale && { lang: locale.split("-")[0], ...options })
}

/**
 * Counts the number of decimal places in a given number.
 *
 * Supports numbers in both standard and exponential notation.
 *
 * @param {number} value - The number to analyze.
 * @returns {number} The count of decimal places.
 *
 * @example
 * countDecimals(12.345) // 3
 * countDecimals(1.2e-4) // 4
 * countDecimals(100) // 0
 */
function countDecimals(value) {
  const text = value.toString()

  if (text.indexOf("e-") > -1) {
    const [, trail] = text.split("e-")
    const deg = parseInt(trail, 10)
    return deg
  }

  if (Math.floor(value) !== value) {
    return value.toString().split(".")[1].length || 0
  }

  return 0
}

/**
 * Checks if two numbers are approximately equal up to a specified number of decimal places.
 *
 * @param {number} num1 - The first number.
 * @param {number} num2 - The second number.
 * @param {number} decimalPlace - The number of decimal places for comparison.
 * @returns {boolean} True if the numbers are approximately equal, false otherwise.
 *
 * @example
 * isApproximatelyEqual(1.2345, 1.2344, 3) // true
 * isApproximatelyEqual(1.2345, 1.233, 3) // false
 */
function isApproximatelyEqual(num1, num2, decimalPlace) {
  const factor = Math.pow(10, decimalPlace)
  return Math.abs(num1 - num2) < 1 / factor
}

/**
 * Checks if the first number is less than the second number.
 *
 * Returns `false` if either argument is not a number.
 *
 * @param {number} num1 - The first number.
 * @param {number} num2 - The second number.
 * @returns {boolean} True if `num1` is less than `num2`, otherwise false.
 *
 * @example
 * isLessThan(3, 5) // true
 * isLessThan(10, 5) // false
 * isLessThan("3", 5) // false
 */
function isLessThan(num1, num2) {
  return typeof num1 === "number" && typeof num2 === "number" && num1 < num2
}

/**
 * Checks if the first number is greater than the second number.
 *
 * Returns `false` if either argument is not a number.
 *
 * @param {number} num1 - The first number.
 * @param {number} num2 - The second number.
 * @returns {boolean} True if `num1` is greater than `num2`, otherwise false.
 *
 * @example
 * isGreaterThan(5, 3) // true
 * isGreaterThan(2, 4) // false
 * isGreaterThan("5", 3) // false
 */
function isGreaterThan(num1, num2) {
  return typeof num1 === "number" && typeof num2 === "number" && num1 > num2
}

/**
 * Checks if the first number is less than or equal to the second number.
 *
 * Returns `false` if either argument is not a number.
 *
 * @param {number} num1 - The first number.
 * @param {number} num2 - The second number.
 * @returns {boolean} True if `num1` is less than or equal to `num2`, otherwise false.
 *
 * @example
 * isLessThanOrEqual(3, 5) // true
 * isLessThanOrEqual(5, 5) // true
 * isLessThanOrEqual(7, 5) // false
 * isLessThanOrEqual("3", 5) // false
 */
function isLessThanOrEqual(num1, num2) {
  return typeof num1 === "number" && typeof num2 === "number" && num1 <= num2
}

/**
 * Checks if the first number is greater than or equal to the second number.
 *
 * Returns `false` if either argument is not a number.
 *
 * @param {number} num1 - The first number.
 * @param {number} num2 - The second number.
 * @returns {boolean} True if `num1` is greater than or equal to `num2`, otherwise false.
 *
 * @example
 * isGreaterThanOrEqual(5, 3) // true
 * isGreaterThanOrEqual(5, 5) // true
 * isGreaterThanOrEqual(2, 5) // false
 * isGreaterThanOrEqual("5", 3) // false
 */
function isGreaterThanOrEqual(num1, num2) {
  return typeof num1 === "number" && typeof num2 === "number" && num1 >= num2
}

/**
 * Checks if the given value is a finite number.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} Returns true if the value is a number and finite, otherwise false.
 *
 * @example
 * isNumber(123); // true
 * isNumber('123'); // false
 * isNumber(NaN); // false
 * isNumber(Infinity); // false
 */
function isNumber(value) {
  return typeof value === "number" && isFinite(value)
}

export {
  isNumber,
  countDecimals,
  extractNumberFromString,
  formatDecimal,
  formatDecimalToWords,
  formatPercentage,
  isApproximatelyEqual,
  isGreaterThan,
  isGreaterThanOrEqual,
  isLessThan,
  isLessThanOrEqual,
  padNumber,
  roundAccurately,
}
